import { motion } from 'framer-motion'
import { MessageSquare, ArrowRight, Bot, User, Mail, Calendar, Search, FileText, Sparkles, Check } from 'lucide-react'
import { useState } from 'react'

interface StepProps {
  onNext: () => void
}

interface Message {
  id: number
  type: 'user' | 'assistant'
  content: string
}

interface TaskCard {
  id: string
  icon: typeof Mail
  title: string
  description: string
  userMessage: string
  response: string
}

const taskCards: TaskCard[] = [
  {
    id: 'email',
    icon: Mail,
    title: 'Draft Email',
    description: 'Professional follow-up',
    userMessage: 'Draft a follow-up email',
    response: "Sure! I'll draft that email in your style:\n\nSubject: Quick Follow-up\n\nHi there,\n\nJust wanted to touch base after our conversation. I've been thinking about the points you raised and have some ideas to share.\n\nWould you have time for a quick call this week?\n\nBest regards"
  },
  {
    id: 'plan',
    icon: Calendar,
    title: 'Plan Week',
    description: 'Optimized schedule',
    userMessage: 'Plan my week',
    response: "Here's an optimized plan for your week:\n\n**Monday:** Deep work session (9-12), Team standup (2pm)\n**Tuesday:** Client meetings, Documentation\n**Wednesday:** Code reviews, 1:1s\n**Thursday:** Sprint planning, Research time\n**Friday:** Wrap-up, Weekly review, Learning time\n\nI've blocked focus time based on your productivity patterns!"
  },
  {
    id: 'research',
    icon: Search,
    title: 'Research Topic',
    description: 'AI trends analysis',
    userMessage: 'Research AI trends',
    response: "I've researched that topic for you. Here are the key findings:\n\n1. **Market Trends:** Growing 15% annually\n2. **Key Players:** 3 major competitors identified\n3. **Opportunities:** Emerging niches in B2B segment\n4. **Risks:** Regulatory changes in Q3\n\nWant me to create a detailed report?"
  },
  {
    id: 'summarize',
    icon: FileText,
    title: 'Summarize',
    description: 'Quick document summary',
    userMessage: 'Summarize this document',
    response: "Here's a concise summary:\n\n**Key Points:**\n- Main objective clearly defined in section 1\n- Three action items identified with deadlines\n- Budget approved with 10% contingency\n- Next review scheduled for end of month\n\n**Action Required:** Sign-off needed from stakeholders by Friday."
  }
]

const Step5 = ({ onNext }: StepProps) => {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: 1,
      type: 'assistant',
      content: "Hi! I'm your AI Twin. Select a task below to see how I can help you work smarter."
    }
  ])
  const [isTyping, setIsTyping] = useState(false)
  const [selectedTask, setSelectedTask] = useState<string | null>(null)

  const handleTaskSelect = (task: TaskCard) => {
    if (isTyping || selectedTask === task.id) return

    setSelectedTask(task.id)

    const userMessage: Message = {
      id: messages.length + 1,
      type: 'user',
      content: task.userMessage
    }

    setMessages((prev) => [...prev, userMessage])
    setIsTyping(true)

    setTimeout(() => {
      const assistantMessage: Message = {
        id: messages.length + 2,
        type: 'assistant',
        content: task.response
      }

      setMessages((prev) => [...prev, assistantMessage])
      setIsTyping(false)
    }, 1500)
  }

  return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center px-4">
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: 'spring', duration: 0.6 }}
        className="w-20 h-20 rounded-2xl bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center mb-6 shadow-xl shadow-primary-500/30"
      >
        <MessageSquare className="w-10 h-10 text-white" />
      </motion.div>

      <motion.h2
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="text-3xl md:text-4xl font-bold text-center mb-4"
      >
        <span className="gradient-text">Interactive Demo</span>
      </motion.h2>

      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="text-lg text-surface-600 dark:text-surface-400 text-center max-w-2xl mb-8"
      >
        Tap a task card to see your AI Twin in action
      </motion.p>

      {/* Task Selection Cards */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 max-w-3xl w-full mb-6"
      >
        {taskCards.map((task, index) => (
          <motion.button
            key={task.id}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5 + index * 0.1 }}
            onClick={() => handleTaskSelect(task)}
            disabled={isTyping}
            className={`relative p-4 rounded-xl border-2 transition-all duration-300 text-left group ${
              selectedTask === task.id
                ? 'border-primary-500 bg-primary-500/10 shadow-lg shadow-primary-500/20'
                : 'border-surface-200 dark:border-surface-700 bg-white dark:bg-surface-800 hover:border-primary-300 dark:hover:border-primary-700 hover:shadow-md'
            } ${isTyping ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
          >
            {selectedTask === task.id && (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-primary-500 flex items-center justify-center"
              >
                <Check className="w-4 h-4 text-white" />
              </motion.div>
            )}
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 transition-all duration-300 ${
              selectedTask === task.id
                ? 'bg-primary-500 text-white'
                : 'bg-primary-500/10 text-primary-500 group-hover:bg-primary-500/20'
            }`}>
              <task.icon className="w-5 h-5" />
            </div>
            <h4 className="font-semibold text-surface-900 dark:text-surface-100 mb-1">
              {task.title}
            </h4>
            <p className="text-xs text-surface-500 dark:text-surface-400">
              {task.description}
            </p>
          </motion.button>
        ))}
      </motion.div>

      {/* Chat Interface */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="w-full max-w-2xl card overflow-hidden"
      >
        {/* Chat Header */}
        <div className="px-6 py-4 border-b border-surface-200 dark:border-surface-700 flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center">
            <Bot className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-surface-900 dark:text-surface-100">Your AI Twin</h3>
            <div className="flex items-center gap-1 text-sm text-green-500">
              <span className="w-2 h-2 rounded-full bg-green-500" />
              Online
            </div>
          </div>
        </div>

        {/* Messages */}
        <div className="h-72 overflow-y-auto p-6 space-y-4 scrollbar-hide">
          {messages.map((message) => (
            <motion.div
              key={message.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`flex items-start gap-3 ${message.type === 'user' ? 'flex-row-reverse' : ''}`}
            >
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                  message.type === 'user'
                    ? 'bg-surface-200 dark:bg-surface-700'
                    : 'bg-gradient-to-br from-primary-500 to-accent-500'
                }`}
              >
                {message.type === 'user' ? (
                  <User className="w-4 h-4 text-surface-600 dark:text-surface-400" />
                ) : (
                  <Bot className="w-4 h-4 text-white" />
                )}
              </div>
              <div
                className={`max-w-[80%] p-4 rounded-2xl ${
                  message.type === 'user'
                    ? 'bg-gradient-to-r from-primary-500 to-accent-500 text-white rounded-br-md'
                    : 'bg-surface-100 dark:bg-surface-800 rounded-bl-md'
                }`}
              >
                <p className="text-sm whitespace-pre-wrap">{message.content}</p>
              </div>
            </motion.div>
          ))}

          {isTyping && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-start gap-3"
            >
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center">
                <Bot className="w-4 h-4 text-white" />
              </div>
              <div className="bg-surface-100 dark:bg-surface-800 rounded-2xl rounded-bl-md p-4">
                <div className="flex gap-1">
                  <motion.span
                    animate={{ opacity: [0.4, 1, 0.4] }}
                    transition={{ repeat: Infinity, duration: 1, delay: 0 }}
                    className="w-2 h-2 rounded-full bg-surface-400"
                  />
                  <motion.span
                    animate={{ opacity: [0.4, 1, 0.4] }}
                    transition={{ repeat: Infinity, duration: 1, delay: 0.2 }}
                    className="w-2 h-2 rounded-full bg-surface-400"
                  />
                  <motion.span
                    animate={{ opacity: [0.4, 1, 0.4] }}
                    transition={{ repeat: Infinity, duration: 1, delay: 0.4 }}
                    className="w-2 h-2 rounded-full bg-surface-400"
                  />
                </div>
              </div>
            </motion.div>
          )}
        </div>

        {/* Bottom hint */}
        <div className="px-6 py-4 border-t border-surface-200 dark:border-surface-700 bg-surface-50 dark:bg-surface-800/50">
          <div className="flex items-center justify-center gap-2 text-surface-500 dark:text-surface-400">
            <Sparkles className="w-4 h-4" />
            <span className="text-sm">Select a task above to see the response</span>
          </div>
        </div>
      </motion.div>

      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.8 }}
        onClick={onNext}
        className="btn-primary text-lg group mt-8"
      >
        Get Started
        <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
      </motion.button>
    </div>
  )
}

export default Step5
